<template>
    <div :class="{ hidden: !checked || !loaded }">
        <div
            v-if="isPlayableAudio"
            class="p-4 border border-solid"
            :class="{
                'bg-sky-100 border-sky-200 rounded-l-lg rounded-tr-lg rounded-br-sm': props.direction === 'outbound',
                'bg-slate-100 border-slate-200 rounded-r-lg rounded-tl-lg rounded-sm': props.direction === 'inbound',
            }"
        >
            <audio controls @loadeddata="loaded = true">
                <source :src="props.mediaUrl" :type="props.mediaContentType" @error="onObjectError" />
            </audio>
        </div>
        <message-attachment
            v-else
            :direction="props.direction"
            :mediaUrl="props.mediaUrl"
            :label="attachmentLabel"
            :expired="isFileExpired"
            :hasPreview="showImagePreview || showVideoPreview"
            @click.stop="onAttachmentClick"
        >
            <img
                v-if="showImagePreview"
                class="block h-auto rounded-md max-w-[240px] max-h-[440px]"
                :src="props.mediaUrl"
                :alt="`message_image`"
                @load="loaded = true"
                @error="onObjectError"
            />
            <video
                v-else-if="showVideoPreview"
                controls
                class="block rounded-md max-w-[240px]"
                @loadeddata="loaded = true"
            >
                <source :src="props.mediaUrl" :type="props.mediaContentType" @error="onObjectError" />
            </video>
        </message-attachment>
    </div>
</template>

<script setup>
import MessageAttachment from './MessageAttachment.vue';
import { ref, computed, onMounted } from 'vue';

const props = defineProps({
    mediaUrl: {
        type: String,
        required: true,
    },
    mediaContentType: {
        type: String,
        default: '',
    },
    direction: {
        type: String,
        required: true,
    },
});

const emit = defineEmits(['attachmentClick']);

const isImage = computed(() =>
    Boolean(
        props.mediaUrl?.toLowerCase()?.match(/\.(png|jpg|jpeg|gif|bmp)$/) ||
            (props.mediaContentType || '').toLowerCase().includes('image')
    )
);
const isVideo = computed(() =>
    Boolean(
        props.mediaUrl?.toLowerCase().match(/\.(mp4|webm)$/) ||
            (props.mediaContentType || '').toLowerCase().includes('video')
    )
);
const isAudio = computed(() =>
    Boolean(
        props.mediaUrl?.toLowerCase().match(/\.(mp3|mpeg|ogg|wav)$/) ||
            (props.mediaContentType || '').toLowerCase().includes('audio')
    )
);

const isMediaSupported = ref(true);
const isFileExpired = ref(false);
const checked = ref(false);
const loaded = ref(false);

const isRenderableMedia = computed(() => !isFileExpired.value && isMediaSupported.value);

const isPlayableAudio = computed(() => isRenderableMedia.value && isAudio.value);
const showImagePreview = computed(() => isRenderableMedia.value && isImage.value);
const showVideoPreview = computed(() => isRenderableMedia.value && !isImage.value && isVideo.value);

const FILE_TYPE_PATTERN = /^[a-z0-9]{1,5}$/;

const FILE_TYPE_BY_MIME_TYPE = {
    'application/pdf': 'PDF',
    'text/csv': 'CSV',
    'text/directory': 'VCF',
    'text/vcard': 'VCF',
    'text/x-vcard': 'VCF',
};

const FILE_TYPE_BY_EXTENSION = {
    vcard: 'VCF',
};

const fileTypeFromContentType = (mediaContentType) => {
    const mimeType = (mediaContentType || '').toLowerCase().split(';')[0].trim();
    const subtype = mimeType.split('/')[1] || '';

    return FILE_TYPE_BY_MIME_TYPE[mimeType] ?? (FILE_TYPE_PATTERN.test(subtype) ? subtype.toUpperCase() : '');
};

const fileTypeFromUrl = (mediaUrl) => {
    const path = (mediaUrl || '').toLowerCase().split('?')[0].split('#')[0].split('/').pop() || '';
    const segments = path.split('.');
    const extension = segments.length > 1 ? segments.pop() : '';

    return FILE_TYPE_BY_EXTENSION[extension] ?? (FILE_TYPE_PATTERN.test(extension) ? extension.toUpperCase() : '');
};

const attachmentLabel = computed(() => {
    const fileType = fileTypeFromContentType(props.mediaContentType) || fileTypeFromUrl(props.mediaUrl);

    return fileType ? `${fileType} Attachment` : 'Attachment';
});

const onAttachmentClick = () => {
    if (isFileExpired.value) {
        emit('attachmentClick', { isFileExpired: true });
    }
};

const checkMedia = async () => {
    try {
        const res = await fetch(props.mediaUrl);
        if (!res.ok) {
            isFileExpired.value = true;
            loaded.value = true;
        }
    } catch (error) {
        console.error(error);

        isFileExpired.value = true;
        loaded.value = true;
    } finally {
        checked.value = true;

        if (!isImage.value && !isVideo.value && !isAudio.value) {
            loaded.value = true;
        }
    }
};

const onObjectError = async () => {
    try {
        const res = await fetch(props.mediaUrl);
        if (res.ok) {
            isMediaSupported.value = false;
        } else {
            isFileExpired.value = true;
        }
    } catch (error) {
        console.error(error);
    } finally {
        loaded.value = true;
    }
};

onMounted(() => {
    checkMedia();
});
</script>
